import merge                                                                                           from 'lodash-es/merge';
import memoize                                                                                         from 'lodash-es/memoize';
import { cloneDeep }                                                                                   from 'lodash-es';
import { GlobalContexts, useGlobals }                                                                  from '@/hooks/use-globals';
import { ChartDataFailureReason, chartWithUuid, DTEOP, LEAD_TIME }                                     from '@/helpers/variables';
import { ChartConfigSeries,
  ChartConfigSeriesData,
  ChartDataResponse,
  ChartDataValidation,
  ChartLegend,
  ChartSingleData,
  DataZoomInsideConfig,
  DataZoomSliderConfig,
  DetailedChartConfig,
  GeneralChartConfiguration }                                                                          from '@/components/chart/chart.types';
import { ChartPosition, ChartValues, DataZoomAxis, DataZoomConfig, DataZoomType, ScrollStartPosition } from '@/components/chart/helpers/chart-variables';
import { DataZoom, ScrollPosition }                                                                    from '@/components/chart/chart-wrapper/store/chart-wrapper.state.store';
import { ObjectWithStringKeys }                                                                        from '@/types';
import { useTooltipContent }                                                                           from '@/components/chart/hooks/use-tooltip-content';
import { useChartToolbox }                                                                             from '@/components/chart/hooks/use-chart-toolbox';
import { getCSSVariable, rgbStringToHex }                                                              from '@/styles/variables.helpers';
import roundNumberFilter                                                                               from '@/helpers/helpers';
import { computed } from '@vue/composition-api';

import Format = echarts.EChartOption.Tooltip.Format;

export const createXAxisDataArray = memoize((chartDataArray: ChartDataResponse[]): string[] => {
  const xAxisDataArray: string[] = [];
  let min!: number;
  let max!: number;

  if (chartDataArray.length) {
    chartDataArray.forEach((singleChartData: ChartDataResponse) => {
      const keysArray: number[] = [];

      singleChartData.data.forEach((data: ChartSingleData) => {
        if (!Number.isNaN(Number(data.key))) {
          keysArray.push(Number(data.key));
        }
      });

      const localMin: number = Math.min(...keysArray);
      const localMax: number = Math.max(...keysArray);

      if (min === undefined) {
        min = localMin;
      }

      if (max === undefined) {
        max = localMax;
      }

      if (localMin < min) {
        min = localMin;
      }

      if (localMax > max) {
        max = localMax;
      }
    });

    for (let i: number = min; i <= max; i += 1) {
      xAxisDataArray.push(String(i));
    }
  }

  return xAxisDataArray;
});

const validateChartData = (singleDataSet: ChartDataResponse, xAxisDataArray: string[]): ChartDataValidation => {
  const reasons: ChartDataFailureReason[] = [];

  const singleDataSetKeys: string[] = singleDataSet.data.reduce((acc: string[], curr: ChartSingleData) => {
    if (curr.value === Infinity) {
      reasons.push(ChartDataFailureReason.INFINITY);
    }
    return [ ...acc, curr.key ];
  }, []);

  if (singleDataSet.data.length !== xAxisDataArray.length) {
    reasons.push(ChartDataFailureReason.NUMBER_OF_ELEMENTS);
  }

  singleDataSetKeys.forEach((key: string) => {
    if (!xAxisDataArray.includes(key)) {
      reasons.push(ChartDataFailureReason.WRONG_KEYS);
    }
  });

  return { isDataOK: reasons.length === 0, reasons };
};

const matchSeriesDataToXAxisData = (singleDataSet: ChartDataResponse, xAxisDataArray: string[]): ChartConfigSeriesData[] => {
  const dataSetCopy: ChartDataResponse = cloneDeep(singleDataSet);
  const sortedData: ChartSingleData[] = dataSetCopy.data.sort((a: ChartSingleData, b: ChartSingleData) => (Number(a.key) > Number(b.key) ? 1 : -1));
  return sortedData.reduce((acc: ChartConfigSeriesData[], curr: ChartSingleData, index: number) => [ ...acc, [ xAxisDataArray[ index ], curr.value ]], []);
};

const fixInfinityInData = (singleDataSet: ChartDataResponse): ChartDataResponse => {
  const singleDataSetCopy: ChartDataResponse = cloneDeep(singleDataSet);
  singleDataSetCopy.data = singleDataSetCopy.data.map((data: ChartSingleData) => ({ ...data, value: data.value === Infinity ? null : data.value }));
  return singleDataSetCopy;
};

const fixNumberOfElementsInData = (singleDataSet: ChartDataResponse, xAxisDataArray: string[]): ChartDataResponse => {
  const singleDataSetCopy: ChartDataResponse = cloneDeep(singleDataSet);
  singleDataSetCopy.data = singleDataSetCopy.data.sort((a: ChartSingleData, b: ChartSingleData) => (Number(a.key) > Number(b.key) ? 1 : -1));
  const fixedData: ChartSingleData[] = [];
  xAxisDataArray.forEach((xAxisKey: string) => {
    const properData: ChartSingleData[] = singleDataSetCopy.data.filter((data: ChartSingleData) => data.key === xAxisKey);

    if (!properData.length) {
      fixedData.push({ key: xAxisKey, value: null });
    } else {
      fixedData.push(properData[ 0 ]);
    }
  });
  return { ...singleDataSet, data: fixedData };
};

const fixWrongKeysInData = (singleDataSet: ChartDataResponse, xAxisDataArray: string[]): ChartDataResponse => {
  const fixedData: ChartSingleData[] = [];
  singleDataSet.data.forEach((data: ChartSingleData) => {
    if (!Number.isNaN(Number(data.key))) {
      fixedData.push(data);
    }
  });
  /* eslint-disable-next-line @typescript-eslint/no-use-before-define */
  return useChartOptionsContent.fixNumberOfElementsInData({ ...singleDataSet, data: fixedData }, xAxisDataArray);
};


const makeReasonsListUniqueAndSorted = (reasonsList: ChartDataFailureReason[]): ChartDataFailureReason[] => {
  const uniqueReasonsSet: Set<ChartDataFailureReason> = new Set(reasonsList);
  let uniqueReasonsArray: ChartDataFailureReason[] = [ ...uniqueReasonsSet ];

  if (uniqueReasonsArray.includes(ChartDataFailureReason.NUMBER_OF_ELEMENTS)) {
    const numberOfElementsIndex: number = uniqueReasonsArray.findIndex((item: ChartDataFailureReason) => item === ChartDataFailureReason.NUMBER_OF_ELEMENTS);
    uniqueReasonsArray.splice(numberOfElementsIndex, 1);
    uniqueReasonsArray = [ ChartDataFailureReason.NUMBER_OF_ELEMENTS, ...uniqueReasonsArray ];
  }
  return uniqueReasonsArray;
};

const createArraySeries = memoize((chartDataArray: ChartDataResponse[], xAxisDataArray: string[]): ChartConfigSeries[] => chartDataArray.map((singleDataSet: ChartDataResponse) => {
  let data: ChartConfigSeriesData[] = [];
  /* eslint-disable-next-line @typescript-eslint/no-use-before-define */
  const { isDataOK, reasons }: { isDataOK: boolean; reasons: ChartDataFailureReason[] } = useChartOptionsContent.validateChartData(singleDataSet, xAxisDataArray);

  if (isDataOK) {
    /* eslint-disable-next-line @typescript-eslint/no-use-before-define */
    data = useChartOptionsContent.matchSeriesDataToXAxisData(singleDataSet, xAxisDataArray);
  } else {
    let dataToFix: ChartDataResponse = singleDataSet;
    /* eslint-disable-next-line @typescript-eslint/no-use-before-define */
    const uniqueReasonsArray: ChartDataFailureReason[] = useChartOptionsContent.makeReasonsListUniqueAndSorted(reasons);

    uniqueReasonsArray.forEach((reason: ChartDataFailureReason) => {
      switch (reason) {
      case ChartDataFailureReason.INFINITY:
        /* eslint-disable-next-line @typescript-eslint/no-use-before-define */
        dataToFix = useChartOptionsContent.fixInfinityInData(cloneDeep(dataToFix));
        /* eslint-disable-next-line @typescript-eslint/no-use-before-define */
        data = useChartOptionsContent.matchSeriesDataToXAxisData(dataToFix, xAxisDataArray);
        break;
      case ChartDataFailureReason.NUMBER_OF_ELEMENTS:
        /* eslint-disable-next-line @typescript-eslint/no-use-before-define */
        dataToFix = useChartOptionsContent.fixNumberOfElementsInData(cloneDeep(dataToFix), xAxisDataArray);
        /* eslint-disable-next-line @typescript-eslint/no-use-before-define */
        data = useChartOptionsContent.matchSeriesDataToXAxisData(dataToFix, xAxisDataArray);
        break;
      case ChartDataFailureReason.WRONG_KEYS:
        /* eslint-disable-next-line @typescript-eslint/no-use-before-define */
        dataToFix = useChartOptionsContent.fixWrongKeysInData(cloneDeep(dataToFix), xAxisDataArray);
        /* eslint-disable-next-line @typescript-eslint/no-use-before-define */
        data = useChartOptionsContent.matchSeriesDataToXAxisData(dataToFix, xAxisDataArray);
        break;
      default:
        break;
      }
    });
  }

  return {
    data,
    name: singleDataSet.functionLabel ? singleDataSet.functionLabel : singleDataSet.function,
    type: ChartValues.LINE,
    itemStyle: {
      color: singleDataSet.color,
    },
    showSymbol: false,
  };
}));

const createLegend = memoize((chartData: ChartDataResponse[]): ChartLegend[] => {
  const legendDataArray: ChartLegend[] = chartData.map((el: ChartDataResponse) => ({
    name: el.functionLabel ? el.functionLabel : el.function,
    icon: ChartValues.CIRCLE,
  }));

  legendDataArray.sort((a: ChartLegend, b: ChartLegend) => (a.name > b.name ? 1 : -1));

  return legendDataArray;
});

const getDataZoomColor = (opacity = 1): string => `rgb(255, 115, 45, ${opacity})`;

export const getDataZoomInsideConfig = (type: DataZoomType, scrollPosition: ScrollPosition<DataZoomAxis>): DataZoomInsideConfig => {
  const properAxisIndex = `${scrollPosition.axis}Index`;

  return {
    type,
    end: scrollPosition.end,
    start: scrollPosition.start,
    customName: properAxisIndex,
    [ properAxisIndex ]: 0,
  };
};

export const getDataZoomSliderConfig = (type: DataZoomType, scrollPosition: ScrollPosition<DataZoomAxis>): DataZoomSliderConfig => ({
  ...getDataZoomInsideConfig(type, scrollPosition),
  handleIcon: DataZoomConfig.HANDLE_ICON,
  handleColor: getDataZoomColor(),
  fillerColor: getDataZoomColor(),
  dataBackgroundColor: getDataZoomColor(0),
  backgroundColor: getDataZoomColor(0.2),
  borderColor: getDataZoomColor(0.05),
  showDataShadow: false,
  showDetail: false,
  handleSize: 15,
});

export const useLeadTimeChartConfig = (chartUuid: string): DetailedChartConfig => {
  const { store }: GlobalContexts = useGlobals();
  const { dataZoom, isScrollRight, chartHeight }: { dataZoom: DataZoom; isScrollRight: boolean; chartHeight: number } = store.state.views.vLeadTime[ chartWithUuid(chartUuid) ];
  const givenChartData: ChartDataResponse[] = store.state.views.vLeadTime[ chartWithUuid(chartUuid) ].chartData.chartDataCollection;
  /* eslint-disable-next-line @typescript-eslint/no-use-before-define */
  const xAxisDataArray: string[] = useChartOptionsContent.createXAxisDataArray(givenChartData);
  /* eslint-disable-next-line @typescript-eslint/no-use-before-define */
  const seriesDataArray: ChartConfigSeries[] = useChartOptionsContent.createArraySeries(givenChartData, xAxisDataArray);
  /* eslint-disable-next-line @typescript-eslint/no-use-before-define */
  const legendDataArray: ChartLegend[] = useChartOptionsContent.createLegend(givenChartData);

  const legendStatus: ObjectWithStringKeys<boolean> = store.state.views.vLeadTime[ chartWithUuid(chartUuid) ].legendSeries;
  const sliderHeight = computed((): string => {
    if (chartHeight) {
      if (chartHeight < 500) return '100px';
      if (chartHeight < 700) return '200px';
    }
    return '55%';
  });

  return {
    animation: !isScrollRight,
    xAxis: {
      data: xAxisDataArray,
    },
    yAxis: {
      type: store.state.views.vLeadTime[ chartWithUuid(chartUuid) ].isLogarithmicAxis ? ChartValues.LOG : ChartValues.VALUE,
    },
    dataZoom: [
      {
        ...getDataZoomSliderConfig(
          DataZoomType.SLIDER,
          {
            ...dataZoom.xAxis,
            end: isScrollRight ? 100 : dataZoom.xAxis.end,
            axis: DataZoomAxis.X_AXIS,
          },
        ),
        height: DataZoomConfig.THICKNESS,
        width: '20%',
        bottom: 10,
        right: '10%',
      },
      {
        ...getDataZoomSliderConfig(
          DataZoomType.SLIDER,
          {
            ...dataZoom.yAxis,
            start: isScrollRight ? 0 : dataZoom.yAxis.start,
            end: isScrollRight ? 100 : dataZoom.yAxis.end,
            axis: DataZoomAxis.Y_AXIS,
          },
        ),
        width: DataZoomConfig.THICKNESS,
        height: sliderHeight.value,
        left: '5%',
      },
      {
        ...getDataZoomInsideConfig(
          DataZoomType.INSIDE,
          {
            ...dataZoom.xAxis,
            end: isScrollRight ? 100 : dataZoom.xAxis.end,
            axis: DataZoomAxis.X_AXIS,
          },
        ),
      },
      {
        ...getDataZoomInsideConfig(
          DataZoomType.INSIDE,
          {
            ...dataZoom.yAxis,
            start: isScrollRight ? 0 : dataZoom.yAxis.start,
            end: isScrollRight ? 100 : dataZoom.yAxis.end,
            axis: DataZoomAxis.Y_AXIS,
          },
        ),
      },
    ],
    series: seriesDataArray,
    legend: {
      data: legendDataArray,
      selected: legendStatus,
    },
    toolbox: useChartToolbox(chartHeight),
  };
};

export const useDteopChartConfig = (chartUuid: string): DetailedChartConfig => {
  const { store }: GlobalContexts = useGlobals();
  const { dataZoom, isScrollRight, chartHeight }: { dataZoom: DataZoom; isScrollRight: boolean; chartHeight: number | null } = store.state.views.vDteop[ chartWithUuid(chartUuid) ];
  const givenChartData: ChartDataResponse[] = store.state.views.vDteop[ chartWithUuid(chartUuid) ].chartData.chartDataCollection;
  /* eslint-disable-next-line @typescript-eslint/no-use-before-define */
  const xAxisDataArray: string[] = useChartOptionsContent.createXAxisDataArray(givenChartData);
  /* eslint-disable-next-line @typescript-eslint/no-use-before-define */
  const seriesDataArray: ChartConfigSeries[] = useChartOptionsContent.createArraySeries(givenChartData, xAxisDataArray);
  /* eslint-disable-next-line @typescript-eslint/no-use-before-define */
  const legendDataArray: ChartLegend[] = useChartOptionsContent.createLegend(givenChartData);

  const legendStatus: ObjectWithStringKeys<boolean> = store.state.views.vDteop[ chartWithUuid(chartUuid) ].legendSeries;
  const sliderHeight = computed((): string => {
    if (chartHeight) {
      if (chartHeight < 500) return '100px';
      if (chartHeight < 700) return '200px';
    }
    return '55%';
  });

  return {
    animation: !isScrollRight,
    xAxis: {
      data: xAxisDataArray,
    },
    yAxis: {
      type: store.state.views.vDteop[ chartWithUuid(chartUuid) ].isLogarithmicAxis ? ChartValues.LOG : ChartValues.VALUE,
    },
    dataZoom: [
      {
        ...getDataZoomSliderConfig(
          DataZoomType.SLIDER,
          {
            ...dataZoom.xAxis,
            end: isScrollRight ? 100 : dataZoom.xAxis.end,
            axis: DataZoomAxis.X_AXIS,
          },
        ),
        height: DataZoomConfig.THICKNESS,
        width: '20%',
        bottom: 10,
        right: '10%',
      },
      {
        ...getDataZoomSliderConfig(
          DataZoomType.SLIDER,
          {
            ...dataZoom.yAxis,
            start: isScrollRight ? 0 : dataZoom.yAxis.start,
            end: isScrollRight ? 100 : dataZoom.yAxis.end,
            axis: DataZoomAxis.Y_AXIS,
          },
        ),
        width: DataZoomConfig.THICKNESS,
        height: sliderHeight.value,
        left: '5%',
      },
      {
        ...getDataZoomInsideConfig(
          DataZoomType.INSIDE,
          {
            ...dataZoom.xAxis,
            end: isScrollRight ? 100 : dataZoom.xAxis.end,
            axis: DataZoomAxis.X_AXIS,
          },
        ),
      },
      {
        ...getDataZoomInsideConfig(
          DataZoomType.INSIDE,
          {
            ...dataZoom.yAxis,
            start: isScrollRight ? 0 : dataZoom.yAxis.start,
            end: isScrollRight ? 100 : dataZoom.yAxis.end,
            axis: DataZoomAxis.Y_AXIS,
          },
        ),
      },
    ],
    series: seriesDataArray,
    legend: {
      data: legendDataArray,
      selected: legendStatus,
    },
    toolbox: useChartToolbox(chartHeight),
  };
};

export const useChartOptions = (view: string, chartUuid: string): GeneralChartConfiguration<ChartConfigSeries> => {
  const { store }: GlobalContexts = useGlobals();
  const dataZoom: DataZoom = {
    xAxis: {
      start: ScrollStartPosition.start,
      end: ScrollStartPosition.end,
    },
    yAxis: {
      start: ScrollStartPosition.start,
      end: ScrollStartPosition.end,
    },
  };

  const chartConfiguration: GeneralChartConfiguration<ChartConfigSeries> = {
    xAxis: {
      type: ChartValues.VALUE,
      data: [],
      axisLine: {
        lineStyle: {
          color: rgbStringToHex(getCSSVariable('--grey-200')),
          width: 2,
        },
      },
      axisLabel: {
        color: rgbStringToHex(getCSSVariable('--black')),
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: rgbStringToHex(getCSSVariable('--grey-100')),
        },
      },
      max: ChartValues.DATAMAX,
      min: ChartValues.DATAMIN,
      splitNumber: 7,
    },
    yAxis: {
      type: ChartValues.VALUE,
      position: ChartPosition.RIGHT,
      axisLabel: {
        color: rgbStringToHex(getCSSVariable('--black')),
        formatter: memoize((value: number) => roundNumberFilter(value)),
      },
      axisLine: {
        lineStyle: {
          color: rgbStringToHex(getCSSVariable('--grey-200')),
          width: 2,
        },
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: rgbStringToHex(getCSSVariable('--grey-100')),
        },
      },
      splitNumber: 4,
    },
    tooltip: {
      trigger: ChartValues.AXIS,
      triggerOn: ChartValues.CLICK,
      backgroundColor: 'rgb(255, 255, 255)',
      padding: 10,
      textStyle: {
        color: '#000',
      },
      extraCssText: 'box-shadow: 0 0 4px rgba(0, 0, 0, 0.3); border-radius: 0; width: fit-content; height: fit-content',
      enterable: true,
      formatter: memoize((params: Format | Format[]): string => useTooltipContent(params)),
    },
    legend: {
      bottom: 0,
      right: '32%',
      left: '6%',
      type: ChartValues.SCROLL,
      itemGap: 30,
      itemWidth: 10,
      pageIconColor: '#FF732D',
      data: [],
      textStyle: {
        color: rgbStringToHex(getCSSVariable('--black')),
      },
    },
    series: [
      {
        data: [],
        type: ChartValues.LINE,
        name: '',
      },
    ],
    dataZoom: [
      {
        ...getDataZoomSliderConfig(
          DataZoomType.SLIDER,
          {
            ...dataZoom.xAxis,
            axis: DataZoomAxis.X_AXIS,
          },
        ),
        filterMode: ChartValues.NONE,
        height: DataZoomConfig.THICKNESS,
        width: '20%',
        bottom: 10,
        right: '5%',
      },
      {
        ...getDataZoomSliderConfig(
          DataZoomType.SLIDER,
          {
            ...dataZoom.yAxis,
            axis: DataZoomAxis.Y_AXIS,
          },
        ),
        filterMode: ChartValues.NONE,
        width: DataZoomConfig.THICKNESS,
        height: '55%',
        left: 10,
      },
      {
        ...getDataZoomInsideConfig(
          DataZoomType.INSIDE,
          {
            ...dataZoom.xAxis,
            axis: DataZoomAxis.X_AXIS,
          },
        ),
        filterMode: ChartValues.NONE,
      },
      {
        ...getDataZoomInsideConfig(
          DataZoomType.INSIDE,
          {
            ...dataZoom.yAxis,
            axis: DataZoomAxis.Y_AXIS,
          },
        ),
        filterMode: ChartValues.NONE,
      },
    ],
    grid: {
      left: '10%',
      right: '10%',
      bottom: 75,
    },
  };

  let detailedChartConfig!: DetailedChartConfig;

  switch (view) {
  case LEAD_TIME:
    /* eslint-disable-next-line no-case-declarations */
    const { isLoading: isLeadTimeLoading }: { isLoading: boolean } = store.state.views.vLeadTime[ chartWithUuid(chartUuid) ].chartData;

    if (!isLeadTimeLoading) {
      /* eslint-disable-next-line @typescript-eslint/no-use-before-define */
      detailedChartConfig = useChartOptionsContent.useLeadTimeChartConfig(chartUuid);
    }
    break;
  case DTEOP:
    /* eslint-disable-next-line no-case-declarations */
    const { isLoading: isDteopLoading }: { isLoading: boolean } = store.state.views.vDteop[ chartWithUuid(chartUuid) ].chartData;

    if (!isDteopLoading) {
      /* eslint-disable-next-line @typescript-eslint/no-use-before-define */
      detailedChartConfig = useDteopChartConfig(chartUuid);
    }
    break;
  default:
    break;
  }

  merge(chartConfiguration, detailedChartConfig);
  return chartConfiguration;
};

export const useChartOptionsContent = {
  useChartOptions,
  useLeadTimeChartConfig,
  createXAxisDataArray,
  createArraySeries,
  validateChartData,
  matchSeriesDataToXAxisData,
  makeReasonsListUniqueAndSorted,
  fixInfinityInData,
  fixWrongKeysInData,
  fixNumberOfElementsInData,
  createLegend,
};
